import {IProtocolStepDefinition} from "../../types/protocolDefinitions";
import {DragDropContext, Draggable, Droppable, DraggableProvided, DraggableStateSnapshot} from "@hello-pangea/dnd";
import {createPortal} from "react-dom";
import {IconGripVertical} from "@tabler/icons-react";
import cx from "clsx";
import classes from "./StepListView.module.css"
import commonClasses from "../../../common/common.module.css"
import {StepIcon} from "../../common/StepIcon";
import React from "react";
import {Box, Text} from "@mantine/core";

import type {BoxProps} from "@mantine/core";

interface StepListViewProps extends BoxProps {
    steps?: Array<IProtocolStepDefinition>
    selectedStep?: IProtocolStepDefinition
    onSelection?: (step: IProtocolStepDefinition) => void,
    onStepsOrderChanged: (steps: Array<IProtocolStepDefinition>) => void
}

// Helper to render draggable content, using portal when dragging to avoid backdrop-filter offset issues
const renderDraggableContent = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    children: React.ReactNode
) => {
    const content = (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
        >
            {children}
        </div>
    );

    // When dragging, render through portal to escape backdrop-filter container
    if (snapshot.isDragging) {
        return createPortal(content, document.body);
    }

    return content;
};

export const StepListView = React.forwardRef<HTMLDivElement, StepListViewProps>(({
                                                                                     steps,
                                                                                     selectedStep,
                                                                                     onSelection = () => null,
                                                                                     onStepsOrderChanged,
                                                                                     ...others
                                                                                 }, ref) => {

        const renderSteps = steps?.map((item, index) => (
            <Draggable key={item.id} index={index} draggableId={item.id}>
                {(provided, snapshot) => renderDraggableContent(
                    provided,
                    snapshot,
                    <div key={item.id}
                         className={cx(classes.row, commonClasses.hover, commonClasses.blur, {[classes.itemDragging]: snapshot.isDragging},
                             item.id === selectedStep?.id && classes.selected)}
                         onClick={() => onSelection(steps[index])}
                         data-testid={`step-${item.id}`}
                    >
                        <div {...provided.dragHandleProps} className={classes.dragHandle}>
                            <IconGripVertical size={"1rem"} stroke={1.5}/>
                        </div>
                        <div className={classes.name}>
                            <Text className={classes.stepNumber} fw={"bold"}>{index + 1}</Text>
                            <div className={classes.stepIcon}>
                                <StepIcon kind={item.kind} size={"1.25rem"}/>
                            </div>
                            <Text>{item.name}</Text>
                        </div>
                    </div>
                )}
            </Draggable>
        ));

        return (
            <Box ref={ref} {...others}>
                <DragDropContext
                    data-testid={"drag-drop-context"}
                    onDragEnd={({destination, source}) => {
                        // we only allow reordering here
                        if (source && destination && steps) {
                            const newSteps = [...steps]
                            const [item] = newSteps.splice(source.index, 1)
                            newSteps.splice(destination?.index || 0, 0, item)
                            onStepsOrderChanged(newSteps)
                        }
                    }}
                >
                    <Droppable droppableId="dnd-list" direction="vertical">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                <div className={classes.stepsContent}>
                                    {renderSteps}
                                </div>
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </Box>
        )
            ;
    }
)